import Table from '@mui/material/Table';
import TableBody from '@mui/material/TableBody';
import TableCell from '@mui/material/TableCell';
import TableContainer from '@mui/material/TableContainer';
import TableRow from '@mui/material/TableRow';
import _ from 'lodash';
import { ChangeEvent, MouseEvent, useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useLocation } from 'react-router-dom';
import { AnyObject } from 'yup';
import { getDefaultSizeSealStamp } from '../../api/sealStamp';
import { SignList } from '../../model/list';
import { Sign } from '../../model/sign';
import { HeadCell, Order } from '../../model/table';
import SysConfigStore from '../../store/common/SysConfigStore';
import SignItem from '../ui/signs/SignItem';
import TableFooter from '../ui/tables/TableFooter';
import TableSignHeader from './TableSignHeader';

// contractId 제거된 테스트용 테이블 컴포넌트

type Props = {
	sealStampId: number;
	signs: Sign[];
	list: SignList[];
	headCells: readonly HeadCell<SignList>[];
	onDrop: (item: AnyObject, dropResult: AnyObject) => void;
};

export default function TestTableSignList({ sealStampId, signs, list, headCells, onDrop }: Props) {
	const { t } = useTranslation();
	const location = useLocation();
	const { isComponentEnabled } = SysConfigStore();
	const [order, setOrder] = useState<Order>('asc');
	const [orderBy, setOrderBy] = useState<keyof SignList>('id');
	const [page, setPage] = useState(0);
	const [rowsPerPage, setRowsPerPage] = useState(5);
	const [signSizes, setSignSizes] = useState<Record<string | number, { width: number; height: number }>>({});
	const [defaultSize, setDefaultSize] = useState<{ width: number; height: number } | null>(null);

	useEffect(() => {
		if (location.pathname.includes('/sign/modify-contract-seal')) {
			getDefaultSizeSealStamp(sealStampId, 100, 100, isComponentEnabled).then(res => {
				setDefaultSize(res);
			});
		}
	}, [location.pathname, sealStampId]);

	const handleRequestSort = (e: MouseEvent<unknown>, property: keyof SignList) => {
		const isAsc = orderBy === property && order === 'asc';
		setOrder(isAsc ? 'desc' : 'asc');
		setOrderBy(property);
	};

	const handleChangePage = (event: unknown, newPage: number) => {
		setPage(newPage);
	};

	const handleChangeRowsPerPage = (e: ChangeEvent<HTMLInputElement>) => {
		setRowsPerPage(parseInt(e.target.value, 10));
		setPage(0);
	};

	const handleImageLoad = (id: string | number, width: number, height: number) => {
		setSignSizes(prev => ({
			...prev,
			[id]: { width, height },
		}));
	};

	const emptyRows = page > 0 ? Math.max(0, (1 + page) * rowsPerPage - list.length) : 0;

	const visibleRows = useMemo(() => {
		const orderArr = order === 'asc' ? _.chain(list).sortBy(orderBy) : _.chain(list).sortBy(orderBy).reverse();

		return {
			count: orderArr.value().length,
			rows: orderArr.slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage).value(),
		};
	}, [list, order, orderBy, page, rowsPerPage]);

	return (
		<section>
			<TableContainer className='overflow-visible'>
				<Table size='medium'>
					<TableSignHeader
						headCells={headCells}
						order={order}
						orderBy={orderBy}
						onRequestSort={handleRequestSort}
					/>
					<TableBody>
						{visibleRows.rows.length === 0 && (
							<TableRow>
								<TableCell colSpan={headCells.length + 1} className='text-center'>
									{t('no_such_data')}
								</TableCell>
							</TableRow>
						)}
						{visibleRows.rows.map(row => {
							let displayWidth = '-';

							if (location.pathname.includes('/sign/modify-contract-seal') && defaultSize) {
								displayWidth = `${defaultSize.width}px`;
							}

							if (signs.length > 0) {
								displayWidth = `${Math.round(signs[0].width)}px`;
							} else if (signSizes[row.id]?.width) {
								displayWidth = `${Math.round(signSizes[row.id].width)}px`;
							}

							return (
								<TableRow hover={row.canDrag} tabIndex={-1} key={row.id}>
									<TableCell align='left' className='relative'>
										<SignItem
											id={sealStampId || row.id}
											name={row.title}
											imageUrl={row.imageUrl}
											canDrag={row.canDrag}
											onDrop={onDrop}
											onImageLoad={handleImageLoad}
										/>
									</TableCell>

									<TableCell align='left'>{displayWidth}</TableCell>
								</TableRow>
							);
						})}
						{emptyRows > 0 && (
							<TableRow style={{ height: 53 * emptyRows }}>
								<TableCell colSpan={headCells.length + 1} />
							</TableRow>
						)}
					</TableBody>
				</Table>
			</TableContainer>
			<TableFooter
				rowsPerPageOptions={[5, 10, 20, 30, 50]}
				showFirstButton
				showLastButton
				count={visibleRows.count}
				rowsPerPage={rowsPerPage}
				page={page}
				onPageChange={handleChangePage}
				onRowsPerPageChange={handleChangeRowsPerPage}
			/>
		</section>
	);
}
